import React, { useEffect, useRef, useState } from 'react';
import { X, ThumbsUp, ThumbsDown, RotateCcw, Loader2 } from 'lucide-react';
import { recordDemoEvent, fetchDemoHtml } from '@/lib/productDemos';

// The demo is authored as a DESKTOP presentation. Rendering the iframe at the
// fixed design canvas (1280×720) and scaling it to fit the screen makes every
// device — phones included — show the EXACT desktop deck: same slides, same
// scene photos, same phone mockups. (Inside the iframe, vw/svh and media
// queries resolve against the 1280×720 canvas, so the demo's own mobile
// overrides never fire.)
const CANVAS_W = 1280;
const CANVAS_H = 720;

// Plays a product demo (the self-contained HTML in /public/demos) and captures a thumbs reaction.
// The demo is a LANDSCAPE walkthrough, so we give it the FULL viewport (a thin bar for title +
// thumbs + close, the demo fills the rest) — cramming it into a small 16:9 box over-rounded the
// device mockup, shrank the images, and left too little room to tap/start the voice. On a phone
// the demo itself shows a "rotate to watch" prompt in portrait. allow="fullscreen; autoplay" lets
// its auto-rotate + narration work.
export default function ProductDemoModal({ demo, subcontractorId, onClose }) {
  const [thumb, setThumb] = useState(null);
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);
  // Vaulted decks (the buyer decks) come back as HTML through a gated RPC and
  // render via srcdoc — no public URL exists for them. Rows with no vaulted
  // HTML (the subcontractor walkthrough films) fall back to their public
  // file_path exactly as before. null = still fetching.
  const [vaulted, setVaulted] = useState(null);
  // Rotate-to-start: the deck is a landscape tour. On a small screen held
  // portrait we cover the stage with a rotate prompt; the first tap after
  // rotating lands inside the iframe, which is what unlocks the narration.
  const [needsRotate, setNeedsRotate] = useState(false);

  useEffect(() => {
    const check = () => {
      const portrait = window.matchMedia('(orientation: portrait)').matches;
      const small = Math.min(window.innerWidth, window.innerHeight) <= 820;
      setNeedsRotate(portrait && small);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => { window.removeEventListener('resize', check); window.removeEventListener('orientationchange', check); };
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const fit = () => {
      const r = el.getBoundingClientRect();
      setScale(Math.min(r.width / CANVAS_W, r.height / CANVAS_H) || 1);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('orientationchange', fit);
    return () => { ro.disconnect(); window.removeEventListener('orientationchange', fit); };
  }, []);

  useEffect(() => {
    if (demo?.key) recordDemoEvent({ subcontractorId, demoKey: demo.key, event: 'view' });
  }, [demo?.key, subcontractorId]);

  useEffect(() => {
    let alive = true;
    setVaulted(null);
    if (!demo?.key) return undefined;
    fetchDemoHtml(demo.key).then((html) => { if (alive) setVaulted({ html: html || null }); });
    return () => { alive = false; };
  }, [demo?.key]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!demo) return null;

  const react = (t) => {
    if (thumb) return;
    setThumb(t);
    recordDemoEvent({ subcontractorId, demoKey: demo.key, event: t === 'up' ? 'thumb_up' : 'thumb_down' });
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950" role="dialog" aria-modal="true" aria-label={demo.title}>
      <div className="flex items-center justify-between gap-3 px-3 sm:px-4 h-12 shrink-0 bg-slate-950 border-b border-white/10">
        <p className="text-sm font-medium text-slate-200 truncate">{demo.title}</p>
        <div className="flex items-center gap-2 shrink-0">
          {thumb ? (
            <span className="text-xs font-medium text-sky-400 mr-1">Thanks for the feedback</span>
          ) : (
            <>
              <span className="hidden sm:inline text-xs text-slate-400 mr-1">Helpful?</span>
              <button type="button" onClick={() => react('up')} aria-label="Helpful"
                className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-300 hover:text-emerald-400 hover:border-emerald-400/40">
                <ThumbsUp className="h-4 w-4" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => react('down')} aria-label="Not helpful"
                className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-300 hover:text-rose-400 hover:border-rose-400/40">
                <ThumbsDown className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
          <button type="button" onClick={onClose} aria-label="Close"
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 hover:bg-white/10 ml-1">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div ref={stageRef} className="relative flex-1 overflow-hidden">
        {vaulted === null ? (
          <div className="absolute inset-0 grid place-items-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
          </div>
        ) : (
          <iframe
            {...(vaulted.html ? { srcDoc: vaulted.html } : { src: demo.file_path })}
            title={demo.title}
            allow="fullscreen; autoplay"
            className="absolute left-1/2 top-1/2 border-0"
            style={{
              width: CANVAS_W,
              height: CANVAS_H,
              transform: `translate(-50%, -50%) scale(${scale})`,
              transformOrigin: 'center center',
            }}
          />
        )}
        {needsRotate && (
          <div className="absolute inset-0 z-10 grid place-items-center bg-slate-950/95 px-8 text-center">
            <div className="grid justify-items-center gap-4 max-w-[26ch]">
              <RotateCcw className="h-14 w-14 animate-pulse text-sky-400" />
              <h3 className="text-xl font-extrabold tracking-tight text-white">Rotate to start</h3>
              <p className="text-sm leading-relaxed text-slate-400">
                This walkthrough plays in landscape. Turn your phone sideways, then tap anywhere to begin with sound.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
